import { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { isGeoColumn } from '../../../../util/table-helper';
import { useGeoFilter } from '../../../../hook/useGeoFilter';
import { t } from 'i18next';
import FilterSelectAll from './FilterSelectAll';
import OptionsList from './OptionsList';
import useGeoSelectionMessage from '../../../../hook/useGeoSelectionMessage';
import { Trans } from 'react-i18next';
import Search from '../../Common/Search';
import { useLocation } from 'react-router-dom';
import { GeoClass } from '../../../../interface/response/GeoFiltersResponse';
import { CASH_REGISTER_URL } from '../../../../util/url';
import { GeoColumns } from '../../../../enum/GeoDivisionColumnName';

interface GeoFiltersProps {
  toggleDropdown: (type: string, index: number) => void;
  isOpen: (type: string, index: number) => boolean;
  filters: any;
  setFilters: (newFilters: {}) => void;
  justClosed: { type: string; index: number } | null;
}

const GeoFilters: React.FC<GeoFiltersProps> = ({ toggleDropdown, isOpen, filters, setFilters, justClosed }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const getMessage = useGeoSelectionMessage();

  // Select all geo options from Redux store (full list and filtered)
  const allOptsFull = useAppSelector((state) => state.filters.allOptionsFull);
  const allOptsFilteredFromStore = useAppSelector((state) => state.filters.allOptionsFiltered);
  const geoFiltersData = useAppSelector((state) => state.filters.geoFiltersData);

  // Memoize columns to avoid recalculating on each render
  const columns = useMemo(() => Object.keys(geoFiltersData).filter(isGeoColumn), [geoFiltersData]);
  const allOptsFiltered = useMemo(() => allOptsFilteredFromStore, [allOptsFilteredFromStore]);

  const {
    handleChange,
    handleDropdownOpen,
    selectAll,
    numberOfSelected,
    filteredGeoData: filteredGeoDataFromHook,
    onFilteredData,
    isDisabled,
  } = useGeoFilter(filters, setFilters, columns, allOptsFiltered);

  // Memoized version of filteredGeoData to prevent unnecessary re-renders
  const filteredGeoData: Record<string, GeoClass[]> = useMemo(() => filteredGeoDataFromHook, [filteredGeoDataFromHook]);
  const prevOptsRef = useRef<Record<string, GeoClass[]>>({});

  // Filter out invalid selections to prevent UI errors
  useEffect(() => {
    if (!filters) return;
    let changed = false;
    const newFilters: Record<string, GeoClass[]> = {};

    columns.forEach((col) => {
      const validOptions: GeoClass[] = allOptsFiltered[col] || [];
      const selected: GeoClass[] = Array.isArray(filters[col]) ? filters[col] : filters[col]?.id ? [filters[col]] : [];
      const validIds = new Set(validOptions.map((opt) => opt.ID));
      const filteredSelection = selected.filter((sel) => validIds.has(sel.ID));
      newFilters[col] = filteredSelection;
      if (selected.length !== filteredSelection.length) changed = true;
    });

    if (changed) setFilters(newFilters); // Update filters only if needed
  }, [allOptsFiltered, columns, filters, setFilters]);

  useEffect(() => {
    if (!filters) return;

    let changed = false;
    const newFilters: Record<string, GeoClass[]> = { ...filters };

    columns.forEach((col) => {
      const currentOptions: GeoClass[] = allOptsFiltered[col] || [];
      const prevOptions = prevOptsRef.current[col];

      // First time we see this column - nothing to compare against yet, just skip
      if (prevOptions === undefined) return;

      const prevIds = new Set(prevOptions.map((o) => o.ID));
      const newOptions = currentOptions.filter((o) => !prevIds.has(o.ID));

      if (newOptions.length === 0) return; // no new options, nothing to do

      const selected: GeoClass[] = Array.isArray(filters[col]) ? filters[col] : filters[col]?.id ? [filters[col]] : [];
      const selectedIds = new Set(selected.map((s) => s.ID));

      // Was everything selected BEFORE this update (every old option was checked)?
      const wasAllSelected = prevOptions.length > 0 && prevOptions.every((opt) => selectedIds.has(opt.ID));

      if (wasAllSelected) {
        newFilters[col] = [...selected, ...newOptions];
        changed = true;
      }
    });

    // Store the current options as the "previous" snapshot for next time
    const prevOptionsMap: Record<string, GeoClass[]> = {};
    columns.forEach((col) => {
      prevOptionsMap[col] = allOptsFiltered[col] || [];
    });
    prevOptsRef.current = prevOptionsMap;

    if (changed) setFilters(newFilters);
  }, [allOptsFiltered, columns, filters, setFilters]);

  // Memoize selected IDs for faster lookup during render
  const selectedIdsMap: Record<string, Set<string>> = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    columns.forEach((col) => {
      const items: GeoClass[] = Array.isArray(filters[col]) ? filters[col] : filters[col]?.id ? [filters[col]] : [];
      map[col] = new Set(items.map((f) => f.ID));
    });
    return map;
  }, [filters, columns]);

  // Memoize "Select All" handlers to prevent recreating functions each render
  const handleSelectAllMap: Record<string, () => void> = useMemo(() => {
    const map: Record<string, () => void> = {};
    columns.forEach((col) => {
      const filteredOptions = filteredGeoData[col] || [];
      map[col] = () => selectAll(col, filteredOptions);
    });
    return map;
  }, [columns, filteredGeoData, selectAll]);

  return (
    <div className="geo_filters filters">
      {columns.map((col, index) => {
        const translatedCol = t(`columns.${col}`);
        const { key, values } = getMessage(translatedCol, numberOfSelected[col]);

        const visibleOptions = allOptsFiltered[col] || [];
        const options = col === columns[0] ? allOptsFull[col] || [] : visibleOptions;

        const isColDisabled = isDisabled(col);
        const isDropdownOpen = isOpen(t('statisticPage.header.filter.dropdown.geo'), index);

        const selectedIds = selectedIdsMap[col];
        const handleSelectAll = handleSelectAllMap[col];
        const filteredOptions: GeoClass[] = filteredGeoData[col] || visibleOptions;

        const hasOptions = filteredOptions.length > 0;

        const allSelected = hasOptions && filteredOptions.every((opt) => selectedIds.has(opt.ID));

        return (
          <div key={`${col}-${index}`} className="filter_select_wrapper">
            <div
              data-pw={`filter${col}`}
              onClick={() => {
                if (isColDisabled) return;
                toggleDropdown(t('statisticPage.header.filter.dropdown.geo'), index);
                handleDropdownOpen(col);
              }}
              className={`filter_select
                ${isColDisabled ? 'disabled' : ''}
                ${isDropdownOpen && !isColDisabled ? 'opened' : ''}
                ${numberOfSelected[col] > 0 ? 'expanded' : ''}
              `}
            >
              <span
                className={`dropdown_title ${!isDropdownOpen || isColDisabled ? 'with_padding' : ''}`}
                key={`${key}-${numberOfSelected}`}
              >
                <Trans
                  i18nKey={key}
                  values={values}
                  components={{
                    count: <span className="number_of_selected" />,
                  }}
                />
              </span>

              {isDropdownOpen && !isColDisabled && (
                <div className="options_container" onClick={(e) => e.stopPropagation()}>
                  <Search
                    placeholder={t('general.search')}
                    className="geo_filter_search"
                    data={options}
                    searchKeys={[t('statisticPage.header.filter.dropdown.geoFilters.name')]}
                    onFilteredData={onFilteredData[col]}
                    autoFocus
                  />
                  <div className="options_list">
                    {hasOptions ? (
                      <>
                        {currentPath !== CASH_REGISTER_URL || col !== GeoColumns.GROUP ? (
                          <FilterSelectAll
                            className="select_all_wrapper"
                            checked={allSelected}
                            selectAll={handleSelectAll}
                            title={t('statisticPage.header.filter.dropdown.selectAll')}
                          />
                        ) : null}

                        <OptionsList
                          isFullHeight={currentPath === CASH_REGISTER_URL && col === GeoColumns.GROUP}
                          checkbox
                          column={t(`columns.${col}`)}
                          filters={filters}
                          handleChange={handleChange}
                          filteredOptions={filteredOptions}
                        />
                      </>
                    ) : (
                      <div className="no_results">{t('general.noResultsFound')}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GeoFilters;
